import React from 'react';
import clsx from 'clsx';
import { CircleHelp, Sparkles } from 'lucide-react';
import { useHistory, useLocation } from 'react-router-dom';

import Link from '@dashboard/components/Link';
import { Button } from '@dashboard/components/ui/button';
import { aiEditorUrl, useAI } from '@dashboard/context/AIContext';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import usePage from '@dashboard/hooks/usePage';

import { findSectionGuide, guidesFor } from './AIPanel/aiGuides';

const sectionHeading = section => section.title || section.label;

// Opens the AI editor with the section's guide; one without a guide clears an attached one.
function SectionAIButton({ section, label }) {
  const { setGuide } = useAI();
  const { page } = usePage();
  const { seller } = useAuth();
  const history = useHistory();
  const { pathname, search } = useLocation();

  const openAIEditor = () => {
    const guide = findSectionGuide(guidesFor({ page, seller }), section.tab, section.path);
    setGuide(guide?.key ?? null, { from: 'section' });
    history.push({ pathname: aiEditorUrl(page._id, pathname), search });
  };

  return (
    <Button variant="outline" size="xs" aria-label={`Edit ${label} with AI`} onClick={openAIEditor}>
      <Sparkles aria-hidden="true" />
      AI
    </Button>
  );
}

// Help when the section has an article, then AI for any section of an editor tab (Test payments
// is not one).
export default function SectionActions({ section }) {
  const label = sectionHeading(section);
  if (!section.helpUrl && !section.tab) return null;

  return (
    <div className="flex shrink-0 items-center gap-1.5">
      {section.helpUrl && (
        <Button variant="outline" size="xs" aria-label={`Help with ${label}`} asChild>
          <Link to={section.helpUrl} bare blank>
            <CircleHelp aria-hidden="true" />
            Help
          </Link>
        </Button>
      )}
      {section.tab && <SectionAIButton section={section} label={label} />}
    </div>
  );
}

export function SectionHeading({ section, className }) {
  return (
    <div className={clsx('mb-0.5 flex items-start justify-between gap-3', className)}>
      <h2 className="text-base font-semibold">{sectionHeading(section)}</h2>
      <SectionActions section={section} />
    </div>
  );
}
